const LOG = "[mexcFut]";
const PROXY = `${process.env.REACT_APP_BACKEND_URL}/proxy`;
const REST_BASE = "https://contract.mexc.com";

// официальный апдейт WS-базы: edge
const WS_ENDPOINTS = [
  "wss://contract.mexc.com/edge",
  "wss://contract.mexc.com/ws",
  "wss://wbs.mexc.com/ws",
  "wss://wbs.mexc.com/raw/ws",
];

// ---------- helpers ----------
const mapInterval = (tf) => ({
  // поддерживаются: Min1, Min5, Min15, Min30, Min60, Hour4, Hour8, Day1, Week1, Month1
  "1m": "Min1",
  "5m": "Min5",
  "15m": "Min15",
  "30m": "Min30",
  "1h": "Min60",
  "4h": "Hour4",
  "8h": "Hour8",
  "1d": "Day1",
  "7d": "Week1",
  "30d": "Month1",
}[tf] || "Min1");

const tfToMs = (tf) => {
  switch (tf) {
    case "1m": return 60_000;
    case "5m": return 300_000;
    case "15m": return 900_000;
    case "30m": return 1_800_000;
    case "1h": return 3_600_000;
    case "4h": return 14_400_000;
    case "8h": return 28_800_000;
    case "1d": return 86_400_000;
    case "7d": return 604_800_000;
    case "30d": return 2_592_000_000;
    default: return 60_000;
  }
};

const toMs = (x) => {
  const n = Number(x);
  return n > 2e10 ? n : n * 1000; // сек -> мс, если нужно
};

// нормализуем к началу окна (важно: минус 1мс, чтобы не перескакивать при ровном закрытии)
const startOfCandle = (tsLike, tfMs) => {
  const ms = toMs(tsLike);
  return Math.floor((ms - 1) / tfMs) * tfMs;
};

const toBar = (t, o, h, l, c) => ({
  time: t,
  open: Number(o),
  high: Number(h),
  low: Number(l),
  close: Number(c),
});

// ---------- REST ----------
export async function mexcFuturesFetchHistory({ symbol, interval, limit = 300 }) {
  const tf = mapInterval(interval);
  const tfMs = tfToMs(interval);
  const url = `${PROXY}/${REST_BASE}/api/v1/contract/kline/${encodeURIComponent(symbol)}?interval=${tf}`;

  console.log(LOG, "REST fetch", { symbol, interval, tf, url });

  try {
    // const r = await fetch(url);
    const r = await fetch(url, {
      headers: {
        'ngrok-skip-browser-warning': 'true',
      },
    });
    if (!r.ok) throw new Error(`MEXC FUT HTTP ${r.status}`);
    const json = await r.json();
    if (!json?.success) throw new Error("MEXC FUT bad payload");

    const d = json.data || {};
    const n = Math.min(
      d.time?.length || 0,
      d.open?.length || 0,
      d.high?.length || 0,
      d.low?.length || 0,
      d.close?.length || 0
    );

    const out = [];
    for (let i = 0; i < n; i++) {
      const t0 = startOfCandle(d.time[i], tfMs); // t — секунды (start), приводим к ms и выравниваем
      const o = d.open[i], h = d.high[i], l = d.low[i], c = d.close[i];
      if ([t0, o, h, l, c].every(v => Number.isFinite(Number(v)))) {
        out.push(toBar(t0, o, h, l, c));
      }
    }

    out.sort((a, b) => a.time - b.time);
    const sliced = out.slice(-limit);

    console.log(LOG, "REST done", {
      symbol, interval, inCount: n, outCount: sliced.length,
      sample: sliced.length ? { first: sliced[0], last: sliced[sliced.length - 1] } : null
    });

    return sliced;
  } catch (e) {
    console.warn(LOG, "REST error", e?.message || String(e));
    return [];
  }
}

// ---------- WS ----------
export function mexcFuturesOpenWS({ symbol, interval, onBar }) {
  const tf = mapInterval(interval);
  const tfMs = tfToMs(interval);

  let ws = null;
  let pingTimer = null;
  let stateLogger = null;
  let closedByUser = false;
  let endpointIdx = 0;

  const stateName = (s) =>
    s === 0 ? "CONNECTING" :
    s === 1 ? "OPEN" :
    s === 2 ? "CLOSING" :
    s === 3 ? "CLOSED" : String(s);

  const clearTimers = () => {
    try { clearInterval(pingTimer); } catch {}
    try { clearInterval(stateLogger); } catch {}
    pingTimer = null; stateLogger = null;
  };

  const cleanup = () => {
    clearTimers();
    try { ws?.close(1000, "client close"); } catch {}
    ws = null;
    console.log(LOG, "closed()");
  };

  const subscribe = () => {
    const sub = { method: "sub.kline", param: { symbol, interval: tf } };
    try { ws?.send(JSON.stringify(sub)); console.log(LOG, "→ SUB", sub); } catch (e) {
      console.warn(LOG, "send error", e);
    }
  };

  const connect = (attempt = 1) => {
    const url = WS_ENDPOINTS[endpointIdx % WS_ENDPOINTS.length];
    console.log(LOG, `WS connect attempt #${attempt}`, { url, symbol, interval, tf });

    try {
      ws = new WebSocket(url);

      stateLogger = setInterval(() => {
        if (!ws) return;
        console.log(LOG, "readyState", { state: ws.readyState, name: stateName(ws.readyState) });
        if (ws.readyState === 1 || ws.readyState === 3) { clearInterval(stateLogger); stateLogger = null; }
      }, 400);

      ws.onopen = () => {
        console.log(LOG, "onopen", { url });
        subscribe();

        clearTimers();
        pingTimer = setInterval(() => {
          try {
            if (ws?.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify({ method: "ping" }));
              // console.log(LOG, "→ ping");
            }
          } catch {}
        }, 15_000);
      };

      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);

          // сервисные ответы
          if (msg?.channel?.startsWith?.("rs.") || msg?.channel === "pong" || msg?.method === "pong") return;

          // основной канал kline
          if (msg?.channel === "push.kline" && msg?.data) {
            const d = msg.data; // { t, o, h, l, c, ... }
            const t0 = startOfCandle(d.t, tfMs);
            const bar = toBar(t0, d.o, d.h, d.l, d.c);
            const ok = [bar.time, bar.open, bar.high, bar.low, bar.close].every(v => Number.isFinite(Number(v)));
            if (ok) {
              // console.log(LOG, "msg kline", bar);
              onBar?.(bar);
            } else {
              console.warn(LOG, "bad kline payload", d);
            }
            return;
          }

          // некоторые ноды присылают без channel
          if (msg?.data && typeof msg.data === "object" && "t" in msg.data && "o" in msg.data) {
            const d = msg.data;
            const t0 = startOfCandle(d.t, tfMs);
            const bar = toBar(t0, d.o, d.h, d.l, d.c);
            const ok = [bar.time, bar.open, bar.high, bar.low, bar.close].every(v => Number.isFinite(Number(v)));
            if (ok) onBar?.(bar);
            return;
          }
        } catch { /* ignore parse errors */ }
      };

      ws.onerror = (e) => {
        console.warn(LOG, "onerror", e);
      };

      ws.onclose = (ev) => {
        clearTimers();
        console.log(LOG, "onclose", { code: ev?.code, reason: ev?.reason, wasClean: ev?.wasClean, url, closedByUser });
        if (closedByUser) return;

        // фоллбэк по эндпоинтам на сетевых/рукопожатие ошибках
        if (ev?.code === 1006 || ev?.code === 1005) {
          endpointIdx++;
          console.log(LOG, "fallback →", WS_ENDPOINTS[endpointIdx % WS_ENDPOINTS.length]);
        }

        const backoff = Math.min(5000, attempt * 500);
        setTimeout(() => connect(attempt + 1), backoff);
      };
    } catch (e) {
      console.warn(LOG, "ctor error", e);
      endpointIdx++;
      setTimeout(() => connect(attempt + 1), 400);
    }
  };

  connect(1);

  return () => { closedByUser = true; cleanup(); };
}
